import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { RevealLines } from "./RevealText";
import { Countdown } from "./Countdown";
import { FALLBACK_TICKET_URL } from "../../lib/api";
import { useLang, T } from "../../lib/i18n";

export const Hero = ({ settings }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const { lang } = useLang();
  const t = T[lang];
  const url = settings?.ticket_url || FALLBACK_TICKET_URL;

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        {settings?.hero_video_url ? (
          <video
            data-testid="hero-video"
            className="w-full h-full object-cover scale-105"
            poster={settings.hero_poster_url}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            ref={(el) => {
              if (el) {
                el.muted = true;
                el.play().catch(() => {});
              }
            }}
          >
            <source src={settings.hero_video_url} type="video/mp4" />
            {settings.hero_video_url.endsWith(".mp4") && settings.hero_video_url.startsWith("/") && (
              <source src={settings.hero_video_url.replace(".mp4", ".webm")} type="video/webm" />
            )}
          </video>
        ) : (
          <div
            className="w-full h-full bg-cover bg-center kenburns"
            style={{ backgroundImage: `url(${settings?.hero_poster_url})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-[#0B2530]/70" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col justify-end max-w-[1440px] mx-auto px-5 md:px-10 pb-24 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-white/80 text-xs md:text-sm uppercase tracking-[0.35em] mb-6"
        >
          {t.hero.kicker}
        </motion.p>

        <RevealLines
          key={lang}
          lines={t.hero.lines}
          delay={0.25}
          className="font-serif text-white text-4xl sm:text-5xl md:text-6xl lg:text-[5.25rem] leading-[1.05] tracking-tight max-w-5xl"
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href={url}
            target="_blank"
            rel="noopener"
            data-testid="hero-buy-tickets"
            className="group inline-flex items-center gap-3 rounded-full bg-[#00B4D8] hover:bg-[#33c5e2] text-white text-sm uppercase tracking-[0.2em] px-9 py-4.5 py-4 transition-colors duration-300"
          >
            {t.nav.buy}
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </motion.div>

        <Countdown />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/60"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
